import { zodResolver } from "@hookform/resolvers/zod";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Button } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { ControlProgramOverviewResourceDto as ControlProgramGraphDto } from "api/generated/model";
import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { ViewCancelButton } from "components/stickyFooter/ViewCancelButton";
import { formatToUserIdAndRight } from "utils/accessRights";
import { navigateTo } from "utils/legacyNavigation";

import { useControlProgramQuery } from "../queries/useControlProgramQuery";
import { useUpdateControlProgramMutation } from "../queries/useUpdateControlProgramMutation";
import { buildControlProgramViewUrl } from "../urlUtils";

import { ControlProgramForm } from "./ControlProgramForm";
import {
    ControlProgramFormSchema,
    useControlProgramViewFormSchema,
} from "./useControlProgramViewFormSchema";
import { toUpdatableControlProgram } from "./utils";

export function ControlProgramEditPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const controlProgramId = Number(id);
    const { data: controlProgramDto } =
        useControlProgramQuery(controlProgramId);
    const userHasAccess = controlProgramDto.content.actions.view;

    return (
        <OrgApplicationBar title={t("EditControlProgram_Title")}>
            {userHasAccess ? (
                <View
                    controlProgramId={controlProgramId}
                    data={controlProgramDto}
                />
            ) : (
                <NoAccessCard />
            )}
        </OrgApplicationBar>
    );
}

interface ViewProps {
    controlProgramId: number;
    data: ControlProgramGraphDto;
}

function View({ controlProgramId, data }: ViewProps) {
    const { t } = useTranslation();
    const schema = useControlProgramViewFormSchema();
    const { mutateAsync, isPending } = useUpdateControlProgramMutation();
    const { resource, actions } = data.content;
    const { controlProgram } = resource;
    const userIdAndRights = formatToUserIdAndRight(resource.accessRights);

    const controlEntityIds = data.content.resource.controlEntityIds;

    const values: ControlProgramFormSchema = {
        name: controlProgram.name,
        description: controlProgram.description || "",
        running: controlProgram.running,
        tagIds: resource.tagIds,
        responsibleId: controlProgram.responsibleId ?? null,
        accessRights: userIdAndRights,
        controlEntityIds: controlEntityIds.map((id) => ({ entityId: id })),
    };

    const { control, formState, handleSubmit, trigger } =
        useForm<ControlProgramFormSchema>({
            resolver: zodResolver(schema),
            mode: "onBlur",
            values,
        });

    const handleSubmitClick = handleSubmit(
        async (data) => {
            const updatableControlProgram = toUpdatableControlProgram(
                values,
                data
            );
            await mutateAsync({
                controlProgramId,
                data: updatableControlProgram,
            });
            navigateTo(buildControlProgramViewUrl(controlProgramId));
        },
        (error) => {
            console.log(error);
            emitNotification(
                "error",
                t("ControlProgram_SubmitValidationError")
            );
        }
    );

    return (
        <FormFooterWrapper>
            <ControlProgramForm
                control={control}
                trigger={trigger}
                users={data.linkedResources.users}
                canEdit={actions.edit.content}
                canEditAccessRights={actions.edit.accessRights}
                canEditEntities={actions.edit.entities}
                linkedEntities={data.linkedResources.entities}
            />
            <StickyFooter>
                <Button
                    size="large"
                    variant="primary"
                    disabled={!formState.isDirty}
                    loading={isPending}
                    onClick={() => void handleSubmitClick()}
                >
                    {t("EditControlProgram_Update")}
                </Button>
                <ViewCancelButton
                    isDirty={formState.isDirty}
                    bodyLabel={t("ControlProgram_ConfirmCancel_Body")}
                />
            </StickyFooter>
        </FormFooterWrapper>
    );
}
